import { Button } from "../../../ui/button";
import { ScrollArea } from "../../../ui/scroll-area";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../ui/dialog";
import { useFormik } from "formik";
import { useToast } from "../../../ui/use-toast";
import { Label } from "../../../ui/label";
import { Input } from "../../../ui/input";
import { CopyButton } from "./CopyButton";
import { Avatar, AvatarFallback, AvatarImage } from "../../../ui/avatar";
import { Switch } from "../../../ui/switch";
import React, { useState } from "react";
import { Badge } from "../../../ui/badge";
import { ToastAction } from "../../../../components/ui/toast";
import { axiosPrivate } from "../../../../api/axiosInstance";
import { UPDATEISACTIVED } from "../../../../api/apiConstants";
import { Loader2 } from "lucide-react";
import { format } from "date-fns";

const EditOrganizationManagersForm = ({ isOpen, onOpenChange, organizationManager, onSubmitSuccess }) => {
  const { toast } = useToast();
  // Formik setup
  const [loading, setLoading] = useState(false)

  const updateStatus = async (accountID, isActived, setSubmitting) => {
    try {
      setLoading(true)

      const response = await axiosPrivate.put(UPDATEISACTIVED, {
        accountID: accountID,
        isActived: isActived,
      });

      if (response.status === 200) {
        console.log(response);
        onSubmitSuccess()

        toast({
          title: "Cập nhật thành công",
          action: <ToastAction altText="undo">Ẩn</ToastAction>,
        });
      }
    } catch (error) {
      if (error.response && error.response.data) {
        const serverMessage = error?.response?.data?.message;
        toast({
          variant: "destructive",
          title: "Đã xảy ra lỗi!",
          description: serverMessage,
          action: <ToastAction altText="undo">Ẩn</ToastAction>,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Đã xảy ra lỗi!",
          description: "Đã có lỗi xảy ra, vui lòng thử lại sau.",
          action: <ToastAction altText="undo">Ẩn</ToastAction>,
        });
      }
    } finally {
      onOpenChange(false);
      setLoading(false)
      setSubmitting(false)
    }
  }



  const formik = useFormik({
    initialValues: {
      isActived: organizationManager ? organizationManager.isActived : false,
      accountID: organizationManager ? organizationManager.accountID : ""
    },
    onSubmit: (values, { setSubmitting }) => {
      updateStatus(values.accountID, values.isActived, setSubmitting)
    },
  });
  /* Giải thích: 
  Vấn đề ở đây là formik là một đối tượng được tạo ra bởi hook useFormik, 
  và nó thay đổi mỗi khi component re-render. Khi mình thêm formik vào mảng dependencies của useEffect, 
  nó sẽ chạy mỗi khi formik thay đổi, tức là mỗi khi component re-render. Một cách để giải quyết vấn đề
   này là sử dụng useRef để lưu trữ giá trị formik.setValues và sau đó sử dụng giá trị đó trong useEffect.
   */
  const setValuesRef = React.useRef(formik.setValues);
  // Update formik initialValues when user changes
  React.useEffect(() => {
    setValuesRef.current({
      isActived: organizationManager ? organizationManager.isActived : false,
      accountID: organizationManager ? organizationManager.accountID : ""

    });
  }, [organizationManager]);
  // Handle switch change
  const handleSwitchChange = (field) => (isChecked) => {
    formik.setFieldValue(field, isChecked);

  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="mobile:max-w-screen-tablet">
        {organizationManager && (
          <form onSubmit={formik.handleSubmit} className="space-y-3">
            <DialogHeader>
              <DialogTitle>Thông tin người dùng</DialogTitle>
              <DialogDescription>
                Lưu ý: Bạn chỉ có thể chỉnh sửa trạng thái của người dùng!
              </DialogDescription>
            </DialogHeader>
            <ScrollArea className="h-96 px-10 py-5 shadow-inner ">
              <div className="flex flex-col gap-5">
                {/* Show avatar người dùng */}
                <div className="flex">
                  <div className="grid flex-1 gap-2">
                    <Label htmlFor="avatar">Avatar</Label>
                    <div className="flex items-center space-x-2">
                      <Avatar className="w-20 h-20">
                        <AvatarImage
                          src={organizationManager ? organizationManager.avatar : ""}
                          alt="@avatar"
                        />
                        <AvatarFallback>A</AvatarFallback>
                      </Avatar>
                    </div>
                  </div>
                </div>
                {/* Show id người dùng */}
                <div className="flex">
                  <div className="grid flex-1 gap-2">
                    <Label htmlFor="accountID">ID tài khoản</Label>
                    <div className="flex items-center space-x-2">
                      <Input
                        id="accountID"
                        defaultValue={organizationManager ? organizationManager.accountID : ""}
                        disabled
                      />
                      <CopyButton type="button" code={organizationManager ? organizationManager.accountID : ""} />
                    </div>
                  </div>
                </div>
                {/* Show tên người dùng */}
                <div className="flex">
                  <div className="grid flex-1 gap-2">
                    <Label htmlFor="username">Tên người dùng</Label>
                    <div className="flex items-center space-x-2">
                      <Input
                        id="username"
                        defaultValue={organizationManager ? organizationManager.username : ""}
                        disabled
                      />
                      <CopyButton type="button" code={organizationManager ? organizationManager.username : ""} />
                    </div>
                  </div>
                </div>
                {/* Show email */}
                <div className="flex">
                  <div className="grid flex-1 gap-2">
                    <Label htmlFor="email">Email</Label>
                    <div className="flex items-center space-x-2">
                      <Input
                        id="email"
                        defaultValue={organizationManager ? organizationManager.email : ""}
                        disabled
                      />
                      <CopyButton type="button" code={organizationManager ? organizationManager.email : ""} />
                    </div>
                  </div>
                </div>

                {/* Show ngày tạo */}

                <div className="flex">
                  <div className="grid flex-1 gap-2">
                    <Label htmlFor="create_date">Ngày tạo</Label>
                    <div className="flex items-center space-x-2">
                      <Badge variant={"outline"}>
                        {organizationManager ? format(new Date(organizationManager?.createdAt), 'dd/MM/yyyy, h:mm:ss a') : ""}
                      </Badge>
                      <CopyButton type="button"
                        code={organizationManager ? format(new Date(organizationManager?.createdAt), 'dd/MM/yyyy, h:mm:ss a') : ""}
                      />
                    </div>
                  </div>
                </div>
                {/* Show role thành viên */}
                <div className="flex mb-3">
                  <div className="grid flex-1 gap-2">
                    <Label htmlFor="role">Vai trò</Label>
                    <div className="flex items-center space-x-2">
                      <Badge variant="primary">Quản lý tổ chức</Badge>

                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="isActived"
                    checked={formik.values.isActived}
                    onCheckedChange={handleSwitchChange("isActived")}
                  />
                  {formik.values?.isActived ? (
                    <Label htmlFor="isActived">Đang hoạt động</Label>
                  ) : (
                    <Label htmlFor="isActived">Dừng hoạt động</Label>
                  )}
                </div>


              </div>
            </ScrollArea>
            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="secondary">
                  Đóng
                </Button>
              </DialogClose>
              <Button
                type="submit"
                disabled={formik.isSubmitting}
                variant="green_theme_primary"
              >
                {loading ? (
                  <>
                    <Loader2 className="  animate-spin flex items-center justify-center w-full" />

                  </>
                ) : (
                  "Xác nhận"
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EditOrganizationManagersForm;
